import React from "react";
import { Sun, Moon, Desktop } from "@phosphor-icons/react";
import { useTheme } from "../theme/ThemeContext";
import { useTranslation } from "react-i18next";

/**
 * v0.4.73 — Three-mode theme cycle chip in the header.
 *
 * Click cycles light → dark → system → light. Tooltip + aria-label
 * announce both the current mode AND what one click would switch to,
 * so the chip is operable without recolouring the icon.
 */
const ICON = {
  light: Sun,
  dark: Moon,
  system: Desktop,
};

const nextLabel = (t) => ({
  light: t("ui.header.theme.next_light", { defaultValue: "Switch to dark theme" }),
  dark: t("ui.header.theme.next_dark", { defaultValue: "Switch to system theme" }),
  system: t("ui.header.theme.next_system", { defaultValue: "Switch to light theme" }),
});

const currentLabel = (t) => ({
  light: t("ui.header.theme.light", { defaultValue: "Light" }),
  dark: t("ui.header.theme.dark", { defaultValue: "Dark" }),
  system: t("ui.header.theme.system", { defaultValue: "System" }),
});

export const ThemeToggle = () => {
  const { t } = useTranslation();
  const NEXT_LABEL = nextLabel(t);
  const CURRENT_LABEL = currentLabel(t);
  const { mode, cycle } = useTheme();
  const Icon = ICON[mode] || Sun;
  return (
    <button
      type="button"
      data-testid="header-theme-toggle"
      data-theme-mode={mode}
      onClick={cycle}
      className="border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 hover:border-teal-400 dark:hover:border-teal-500 text-slate-700 dark:text-slate-200 hover:text-teal-700 dark:text-teal-300 dark:hover:text-teal-300 px-2.5 py-1 rounded-full text-xs font-medium flex items-center gap-1.5 transition-colors"
      title={NEXT_LABEL[mode]}
      aria-label={t("ui.header.theme.aria", { current: CURRENT_LABEL[mode], next: NEXT_LABEL[mode], defaultValue: `Theme: ${CURRENT_LABEL[mode]}. ${NEXT_LABEL[mode]}` })}
    >
      <Icon size={12} weight="bold" />
      <span className="hidden sm:inline">{CURRENT_LABEL[mode]}</span>
    </button>
  );
};

export default ThemeToggle;
